import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { Control } from 'react-hook-form';
import { useFormState, useWatch } from 'react-hook-form';
import { theme } from '~/theme';

import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PublishIcon from '@mui/icons-material/Publish';
import SaveIcon from '@mui/icons-material/Save';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Tooltip,
  Typography,
  styled,
} from '@mui/material';

import { type CreateContentComponentTagBodyParams } from '~/services/ContentService';

import {
  CONTENT_TAG_STATUS,
  CONTENT_TAG_STATUS_COLORS,
  CONTENT_TAG_STATUS_LABELS,
  type ContentTagStatus,
  type UpdateComponentTagStatusParams,
  type UpdateComponentTagValuesParams,
  useContentComponentTags,
  useCreateContentComponentTags,
  useUpdateContentComponentTags,
} from '../hooks';

import TagContentForm, { DEFAULT_COMPONENT_TAG_FORM_STATE, type TagContentFormData } from './TagContentForm';

const AccordionSummaryStyled = styled(AccordionSummary)(({ theme }) => ({
  margin: 0,
  padding: theme.spacing(1),
}));

const AccordionDetailsStyled = styled(AccordionDetails)(({ theme }) => ({
  margin: 0,
  padding: theme.spacing(1),
}));

const AccordionStyled = styled(Accordion)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  padding: theme.spacing(1),
  boxShadow: 'none',
  '&:before': {
    display: 'none',
  },
  '&:not(:last-child)': {
    marginBottom: theme.spacing(1),
  },
}));

type Props = {
  tagName: string;
  control: Control<TagContentFormData>;
  onReset?: (data: TagContentFormData) => void;
};

function getStatusLabel(status: ContentTagStatus | undefined): string {
  if (!status) {
    return CONTENT_TAG_STATUS_LABELS[CONTENT_TAG_STATUS.DRAFT];
  }
  return CONTENT_TAG_STATUS_LABELS[status] ?? CONTENT_TAG_STATUS_LABELS[CONTENT_TAG_STATUS.DRAFT];
}

function getStatusColor(status: ContentTagStatus | undefined): string {
  if (!status) {
    return CONTENT_TAG_STATUS_COLORS[CONTENT_TAG_STATUS.DRAFT];
  }
  return CONTENT_TAG_STATUS_COLORS[status] ?? CONTENT_TAG_STATUS_COLORS[CONTENT_TAG_STATUS.DRAFT];
}

type MutationAction = 'save' | 'publish' | 'create' | null;

export default function TagContentUpdate({ tagName, control, onReset }: Props) {
  const appliedRef = useRef(false);
  const [currentAction, setCurrentAction] = useState<MutationAction>(null);

  const formState = useFormState({ control });
  const formData = useWatch({ control });

  const updateMutation = useUpdateContentComponentTags();
  const createMutation = useCreateContentComponentTags();

  const slugs = useMemo(
    () => ({
      offerBanner: `offer-banner-${tagName}`,
      searchBanner: `search-banner-${tagName}`,
      offerPill: `offer-pill-${tagName}`,
      inflowPill: `inflow-pill-${tagName}`,
      searchPill: `search-pill-${tagName}`,
    }),
    [tagName],
  );

  const { offerBanner, searchBanner, pill, offerPill, inflowPill, searchPill, isLoading, isFetching } =
    useContentComponentTags(slugs, {
      enabled: !!tagName,
    });

  const allPillsExist = !!offerPill && !!inflowPill && !!searchPill;

  useEffect(() => {
    if (appliedRef.current) {
      return;
    }
    if (isLoading || isFetching) {
      return;
    }

    appliedRef.current = true;

    const newData: TagContentFormData = {
      offerBanner: {
        icon: offerBanner?.icon || DEFAULT_COMPONENT_TAG_FORM_STATE.icon,
        title: offerBanner?.title ?? '',
        subtitle: offerBanner?.subtitle ?? '',
        linkText: offerBanner?.linkText ?? '',
        status: offerBanner?.status || CONTENT_TAG_STATUS.DRAFT,
      },
      searchBanner: {
        icon: searchBanner?.icon || DEFAULT_COMPONENT_TAG_FORM_STATE.icon,
        title: searchBanner?.title ?? '',
        subtitle: searchBanner?.subtitle ?? '',
        linkText: searchBanner?.linkText ?? '',
        status: searchBanner?.status || CONTENT_TAG_STATUS.DRAFT,
      },
      pill: allPillsExist
        ? {
            icon: pill?.icon || DEFAULT_COMPONENT_TAG_FORM_STATE.icon,
            title: pill?.title ?? '',
            subtitle: pill?.subtitle ?? '',
            linkText: pill?.linkText ?? '',
            status: pill?.status || CONTENT_TAG_STATUS.DRAFT,
          }
        : { ...DEFAULT_COMPONENT_TAG_FORM_STATE },
    };

    if (onReset !== undefined) {
      onReset(newData);
    }
  }, [isLoading, isFetching, offerBanner, searchBanner, pill, allPillsExist, onReset]);

  const handleSave = useCallback(async () => {
    setCurrentAction('save');
    try {
      const valuesParams: Array<UpdateComponentTagValuesParams> = [];
      const statusParams: Array<UpdateComponentTagStatusParams> = [];

      const contentTypeMap = [
        { tag: offerBanner, form: formData.offerBanner, referenceTag: offerBanner },
        { tag: searchBanner, form: formData.searchBanner, referenceTag: searchBanner },
        { tag: offerPill, form: formData.pill, referenceTag: offerPill },
        { tag: inflowPill, form: formData.pill, referenceTag: offerPill },
        { tag: searchPill, form: formData.pill, referenceTag: offerPill },
      ];

      for (const { tag, form, referenceTag } of contentTypeMap) {
        if (!tag?.id || !form) {
          continue;
        }

        const valuesChanged =
          form.icon !== referenceTag?.icon ||
          form.title !== referenceTag?.title ||
          (form.subtitle || '') !== (referenceTag?.subtitle || '') ||
          (form.linkText || '') !== (referenceTag?.linkText || '');

        const statusChanged = form.status !== tag.status;

        if (valuesChanged) {
          valuesParams.push({
            id: tag.id,
            icon: form.icon,
            title: form.title,
            subtitle: form.subtitle,
            linkText: form.linkText,
          });

          if (statusChanged) {
            statusParams.push({ id: tag.id, status: form.status });
          }
        } else if (statusChanged) {
          statusParams.push({ id: tag.id, status: form.status });
        }
      }

      if (valuesParams.length > 0 || statusParams.length > 0) {
        await updateMutation.mutateAsync({ values: valuesParams, statuses: statusParams });
        appliedRef.current = false;
      }
    } finally {
      setCurrentAction(null);
    }
  }, [offerBanner, searchBanner, offerPill, inflowPill, searchPill, formData, updateMutation]);

  const handleCreateMissing = useCallback(async () => {
    setCurrentAction('create');
    try {
      const params: Array<CreateContentComponentTagBodyParams> = [];

      const brand = 'luxuryescapes';

      if (!offerBanner && formData.offerBanner?.title) {
        params.push({
          slug: slugs.offerBanner,
          brand,
          icon: formData.offerBanner.icon || DEFAULT_COMPONENT_TAG_FORM_STATE.icon,
          title: formData.offerBanner.title,
          subtitle: formData.offerBanner.subtitle || undefined,
          linkText: formData.offerBanner.linkText || undefined,
        });
      }

      if (!searchBanner && formData.searchBanner?.title) {
        params.push({
          slug: slugs.searchBanner,
          brand,
          icon: formData.searchBanner.icon || DEFAULT_COMPONENT_TAG_FORM_STATE.icon,
          title: formData.searchBanner.title,
          subtitle: formData.searchBanner.subtitle || undefined,
          linkText: formData.searchBanner.linkText || undefined,
        });
      }

      if (formData.pill?.title) {
        if (!offerPill) {
          params.push({
            slug: slugs.offerPill,
            brand,
            icon: formData.pill.icon || DEFAULT_COMPONENT_TAG_FORM_STATE.icon,
            title: formData.pill.title,
            subtitle: formData.pill.subtitle || undefined,
            linkText: formData.pill.linkText || undefined,
          });
        }
        if (!inflowPill) {
          params.push({
            slug: slugs.inflowPill,
            brand,
            icon: formData.pill.icon || DEFAULT_COMPONENT_TAG_FORM_STATE.icon,
            title: formData.pill.title,
            subtitle: formData.pill.subtitle || undefined,
            linkText: formData.pill.linkText || undefined,
          });
        }
        if (!searchPill) {
          params.push({
            slug: slugs.searchPill,
            brand,
            icon: formData.pill.icon || DEFAULT_COMPONENT_TAG_FORM_STATE.icon,
            title: formData.pill.title,
            subtitle: formData.pill.subtitle || undefined,
            linkText: formData.pill.linkText || undefined,
          });
        }
      }

      const existingPillsToSync: Array<UpdateComponentTagValuesParams> = [];
      const hasMissingPills = !offerPill || !inflowPill || !searchPill;
      if (hasMissingPills && formData.pill?.title) {
        const pillValues = {
          icon: formData.pill.icon || DEFAULT_COMPONENT_TAG_FORM_STATE.icon,
          title: formData.pill.title,
          subtitle: formData.pill.subtitle || '',
          linkText: formData.pill.linkText || '',
        };
        if (offerPill?.id) {
          existingPillsToSync.push({ id: offerPill.id, ...pillValues });
        }
        if (inflowPill?.id) {
          existingPillsToSync.push({ id: inflowPill.id, ...pillValues });
        }
        if (searchPill?.id) {
          existingPillsToSync.push({ id: searchPill.id, ...pillValues });
        }
      }

      const mutations: Array<Promise<unknown>> = [];

      if (params.length > 0) {
        mutations.push(createMutation.mutateAsync(params));
      }

      if (existingPillsToSync.length > 0) {
        const republishStatuses: Array<UpdateComponentTagStatusParams> = existingPillsToSync.map((entry) => ({
          id: entry.id,
          status: CONTENT_TAG_STATUS.PUBLISHED,
        }));
        mutations.push(updateMutation.mutateAsync({ values: existingPillsToSync, statuses: republishStatuses }));
      }

      if (mutations.length > 0) {
        await Promise.all(mutations);
        appliedRef.current = false;
      }
    } finally {
      setCurrentAction(null);
    }
  }, [offerBanner, searchBanner, offerPill, inflowPill, searchPill, formData, slugs, createMutation, updateMutation]);

  const hasExistingDirtyFields =
    (!!offerBanner && !!formState.dirtyFields.offerBanner) ||
    (!!searchBanner && !!formState.dirtyFields.searchBanner) ||
    (allPillsExist && !!formState.dirtyFields.pill);

  const hasExistingValidationErrors =
    (!!offerBanner && !formData.offerBanner?.title) ||
    (!!searchBanner && !formData.searchBanner?.title) ||
    (allPillsExist && !formData.pill?.title);

  const isSaveDisabled =
    !hasExistingDirtyFields || hasExistingValidationErrors || updateMutation.isPending || createMutation.isPending;

  const changedContentTags = [offerBanner, searchBanner, offerPill, inflowPill, searchPill].filter(
    (tag) => tag?.status === CONTENT_TAG_STATUS.CHANGED && tag?.id,
  );
  const hasChangedContent = changedContentTags.length > 0;

  const handlePublishAll = useCallback(async () => {
    setCurrentAction('publish');
    try {
      const statusParams: Array<UpdateComponentTagStatusParams> = [];

      for (const tag of changedContentTags) {
        if (tag?.id) {
          statusParams.push({ id: tag.id, status: CONTENT_TAG_STATUS.PUBLISHED });
        }
      }

      if (statusParams.length > 0) {
        await updateMutation.mutateAsync({ values: [], statuses: statusParams });
        appliedRef.current = false;
      }
    } finally {
      setCurrentAction(null);
    }
  }, [changedContentTags, updateMutation]);

  const offerBannerStatus = offerBanner?.status;
  const searchBannerStatus = searchBanner?.status;
  const pillStatus = allPillsExist ? pill?.status : undefined;

  if (isLoading || isFetching) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  const hasMissingContentTags = !offerBanner || !searchBanner || !offerPill || !inflowPill || !searchPill;

  const hasMissingWithTitle =
    (!offerBanner && !!formData.offerBanner?.title) ||
    (!searchBanner && !!formData.searchBanner?.title) ||
    ((!offerPill || !inflowPill || !searchPill) && !!formData.pill?.title);

  return (
    <Box>
      <Stack direction="row" justifyContent="flex-end" spacing={1} mb={2}>
        {hasMissingContentTags && (
          <Button
            variant="contained"
            color="warning"
            startIcon={currentAction === 'create' ? <CircularProgress size={20} color="inherit" /> : <AddIcon />}
            disabled={currentAction !== null || !hasMissingWithTitle}
            onClick={handleCreateMissing}
          >
            Create Missing Content Tags
          </Button>
        )}
        {hasChangedContent && (
          <Tooltip title="Publish all content with 'Changed' status">
            <Button
              variant="outlined"
              color="primary"
              startIcon={currentAction === 'publish' ? <CircularProgress size={20} color="inherit" /> : <PublishIcon />}
              disabled={currentAction !== null}
              onClick={handlePublishAll}
            >
              Publish
            </Button>
          </Tooltip>
        )}
        <Button
          variant="contained"
          color="primary"
          startIcon={currentAction === 'save' ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
          disabled={isSaveDisabled || currentAction !== null}
          onClick={handleSave}
        >
          Save
        </Button>
      </Stack>
      <AccordionStyled>
        <AccordionSummaryStyled expandIcon={<ExpandMoreIcon />}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="h6">Offer Banner</Typography>
            <Chip
              label={offerBanner ? getStatusLabel(offerBannerStatus) : 'Not Created'}
              size="small"
              sx={{
                bgcolor: offerBanner ? getStatusColor(offerBannerStatus) : theme.palette.error.main,
                color: 'white',
              }}
            />
          </Stack>
        </AccordionSummaryStyled>
        <AccordionDetailsStyled>
          {!offerBanner && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Content tag not yet created. Fill in the fields and click "Create Missing Content Tags".
            </Alert>
          )}
          <TagContentForm control={control} namePrefix="offerBanner" disableStatus={!offerBanner} />
        </AccordionDetailsStyled>
      </AccordionStyled>
      <AccordionStyled>
        <AccordionSummaryStyled expandIcon={<ExpandMoreIcon />}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="h6">Search Banner</Typography>
            <Chip
              label={searchBanner ? getStatusLabel(searchBannerStatus) : 'Not Created'}
              size="small"
              sx={{
                bgcolor: searchBanner ? getStatusColor(searchBannerStatus) : theme.palette.error.main,
                color: 'white',
              }}
            />
          </Stack>
        </AccordionSummaryStyled>
        <AccordionDetailsStyled>
          {!searchBanner && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Content tag not yet created. Fill in the fields and click "Create Missing Content Tags".
            </Alert>
          )}
          <TagContentForm control={control} namePrefix="searchBanner" showLinkText disableStatus={!searchBanner} />
        </AccordionDetailsStyled>
      </AccordionStyled>
      <AccordionStyled>
        <AccordionSummaryStyled expandIcon={<ExpandMoreIcon />}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="h6">Pill (offer-pill, inflow-pill, search-pill)</Typography>
            <Chip
              label={allPillsExist ? getStatusLabel(pillStatus) : 'Not Created'}
              size="small"
              sx={{ bgcolor: allPillsExist ? getStatusColor(pillStatus) : theme.palette.error.main, color: 'white' }}
            />
          </Stack>
        </AccordionSummaryStyled>
        <AccordionDetailsStyled>
          {!allPillsExist && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Content tag not yet created. Fill in the fields and click "Create Missing Content Tags".
            </Alert>
          )}
          <TagContentForm control={control} namePrefix="pill" disableStatus={!allPillsExist} />
        </AccordionDetailsStyled>
      </AccordionStyled>
    </Box>
  );
}
